import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getColors, fonts } from '../theme';
import { Mail, Lock, User, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const LoginScreen = ({ onBack }) => {
  const { 
    isDark, 
    signIn, 
    signUp, 
    resetPassword, 
    signInWithGoogle,
    authError,
    clearAuthError 
  } = useApp();
  const colors = getColors(isDark);
  
  // View state: 'signin' | 'signup' | 'reset'
  const [view, setView] = useState('signin');
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [formError, setFormError] = useState('');

  // Clear errors when switching views
  const switchView = (newView) => {
    setView(newView);
    setFormError('');
    clearAuthError();
    setResetSent(false);
  };

  // Validate password strength
  const validatePassword = (pass) => {
    if (pass.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pass)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(pass)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(pass)) return 'Password must contain a number';
    return null;
  };

  // Handle sign in
  const handleSignIn = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!email || !password) {
      setFormError('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    await signIn(email, password);
    setLoading(false);
  };

  // Handle sign up
  const handleSignUp = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!email || !password || !confirmPassword) {
      setFormError('Please fill in all fields');
      return;
    }
    
    if (!agreedToTerms) {
      setFormError('Please agree to the Terms & Conditions');
      return;
    }
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      setFormError(passwordError);
      return;
    }
    
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    await signUp(email, password, displayName || email.split('@')[0]);
    setLoading(false);
  };

  // Handle password reset
  const handleReset = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!email) {
      setFormError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    const result = await resetPassword(email);
    setLoading(false);
    
    if (result.success) {
      setResetSent(true);
    }
  };

  // Handle OAuth sign in
  const handleOAuth = async (provider) => {
    setLoading(true);
    if (provider === 'google') {
      await signInWithGoogle();
    }
    setLoading(false);
  };

  const error = formError || authError;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-8 relative overflow-hidden"
      style={{ backgroundColor: colors.bg }}
    >

      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-6 left-6 p-3 rounded-md transition-all duration-200 flex items-center gap-2 z-10"
          style={{ 
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            
            color: colors.textMuted
          }}
        >
          <ArrowLeft size={18} />
          <span className="text-sm">Back</span>
        </button>
      )}

      <div className="w-full max-w-sm relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-5xl mb-4"
            style={{ fontFamily: fonts.serif, color: colors.textPrimary, fontWeight: 400 }}
          >
            mind.
          </h1>
          <p 
            className="text-xs font-medium"
            style={{ color: colors.textMuted }}
          >
            Daily Practice Tracker
          </p>
        </div>
        
        {/* Card */}
        <div 
          className="pt-5 pb-2"
          style={{ borderTop: `1px solid ${colors.textPrimary}` }}
        >
          {/* Back button for non-signin views */}
          {view !== 'signin' && (
            <button
              onClick={() => switchView('signin')}
              className="flex items-center gap-2 mb-6 text-sm transition-all duration-200 hover:opacity-70"
              style={{ color: colors.accent }}
            >
              <ArrowLeft size={16} />
              Back to sign in
            </button>
          )}

          {/* View Title */}
          <h2 
            className="text-center text-lg mb-6 font-semibold tracking-tight"
            style={{ color: colors.textPrimary }}
          >
            {view === 'signin' && 'Welcome back'}
            {view === 'signup' && 'Create your account'}
            {view === 'reset' && 'Reset password'}
          </h2>

          {/* Error Message */}
          {error && (
            <div 
              className="mb-6 p-3 rounded-md text-sm text-center"
              style={{ 
                backgroundColor: `${colors.error}15`,
                color: colors.error,
                border: `1px solid ${colors.error}30`
              }}
            >
              {error}
            </div>
          )}

          {/* Reset Success Message */}
          {resetSent && (
            <div 
              className="mb-6 p-3 rounded-md text-sm text-center"
              style={{ 
                backgroundColor: `${colors.accent}15`,
                color: colors.accent,
                border: `1px solid ${colors.accent}30`
              }}
            >
              Password reset email sent! Check your inbox.
            </div>
          )}

          {/* Sign In Form */}
          {view === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full pl-11 pr-4 py-3 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full pl-11 pr-11 py-3 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  style={{ color: colors.textMuted }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              
              <button
                type="button"
                onClick={() => switchView('reset')}
                className="text-xs transition-all duration-200 hover:opacity-70"
                style={{ color: colors.accent }}
              >
                Forgot password?
              </button>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-xs rounded-md transition-all duration-200 disabled:opacity-50 font-medium"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          {/* Sign Up Form */}
          {view === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Display name (optional)"
                  className="w-full pl-11 pr-4 py-3 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full pl-11 pr-4 py-3 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full pl-11 pr-11 py-3 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  style={{ color: colors.textMuted }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="w-full pl-11 pr-4 py-3 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              {/* Password requirements */}
              <div className="text-xs space-y-1" style={{ color: colors.textMuted }}>
                <p>Password must contain:</p>
                <ul className="list-disc list-inside pl-2 space-y-0.5">
                  <li style={{ color: password.length >= 8 ? colors.accent : colors.textMuted }}>
                    At least 8 characters
                  </li>
                  <li style={{ color: /[A-Z]/.test(password) ? colors.accent : colors.textMuted }}>
                    One uppercase letter
                  </li>
                  <li style={{ color: /[a-z]/.test(password) ? colors.accent : colors.textMuted }}>
                    One lowercase letter
                  </li>
                  <li style={{ color: /[0-9]/.test(password) ? colors.accent : colors.textMuted }}>
                    One number
                  </li>
                </ul>
              </div>
              
              {/* Terms checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded"
                  style={{ accentColor: colors.accent }}
                />
                <span className="text-xs leading-relaxed" style={{ color: colors.textMuted }}>
                  I agree to the{' '}
                  <Link 
                    to="/terms" 
                    className="underline hover:opacity-70 transition-opacity"
                    style={{ color: colors.accent }}
                  >
                    Terms & Conditions
                  </Link>
                  {' '}and{' '}
                  <Link 
                    to="/privacy" 
                    className="underline hover:opacity-70 transition-opacity"
                    style={{ color: colors.accent }}
                  >
                    Privacy Policy
                  </Link>
                </span>
              </label>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-xs rounded-md transition-all duration-200 disabled:opacity-50 font-medium"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Password Reset Form */}
          {view === 'reset' && !resetSent && (
            <form onSubmit={handleReset} className="space-y-4">
              <p className="text-sm text-center mb-4" style={{ color: colors.textSecondary }}>
                Enter your email and we'll send you a link to reset your password.
              </p>
              
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full pl-11 pr-4 py-3 text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-xs rounded-md transition-all duration-200 disabled:opacity-50 font-medium"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          {/* Divider */}
          {view === 'signin' && (
            <>
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
                  or continue with
                </span>
                <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
              </div>
              
              {/* OAuth Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => handleOAuth('google')}
                  disabled={loading}
                  className="flex items-center justify-center gap-4 w-full py-3.5 text-xs rounded-md transition-all duration-150 hover:translate-y-[-2px] disabled:opacity-50 font-medium"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
              </div>
            </>
          )}

          {/* Switch between sign in and sign up */}
          {view === 'signin' && (
            <p className="text-center mt-6 text-sm" style={{ color: colors.textMuted }}>
              Don't have an account?{' '}
              <button
                onClick={() => switchView('signup')}
                className="underline hover:opacity-70 transition-opacity"
                style={{ color: colors.accent }}
              >
                Sign up
              </button>
            </p>
          )}
        </div>

        <div className="text-center mt-12">
          <p 
            className="text-xs mb-2"
            style={{ color: colors.textMuted }}
          >
            Your data is encrypted and private
          </p>
          <div className="flex justify-center gap-4">
            <Link 
              to="/privacy" 
              className="text-xs underline hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              className="text-xs underline hover:opacity-70 transition-opacity"
              style={{ color: colors.textMuted }}
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
